/**
 * Analytics — GA4 + Meta Pixel measurement layer for the storefront.
 *
 * Direct gtag/fbq snippets (no GTM container) so the surface is small and
 * easy to audit. Tag scripts are injected by app/components/AnalyticsBootstrap.
 *
 * All public helpers are no-ops when:
 *   - the corresponding measurement ID env var is blank, or
 *   - the corresponding tag isn't on the window yet (e.g. SSR, or the
 *     bootstrap hasn't run on this render).
 *
 * Pricing flows through this module as ALREADY-CONVERTED USD numbers
 * (i.e. dollars). Most call sites have integer-cents from the cart store —
 * divide by 100 at the call site, or pass a price string and let parsePrice
 * normalize "$129.99" / "129.99" / 129.99 into 129.99.
 *
 * Event taxonomy:
 *   - GA4 uses the e-commerce shape: { items[], value, currency: "USD" }
 *   - Meta Pixel uses standard names: ViewContent / AddToCart /
 *     InitiateCheckout / Purchase, with content_ids[] + value + currency.
 */

import type { Product } from "@/app/data/products";
import type { CartLine } from "@/lib/cart-store";

export type AnalyticsItem = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_brand?: string;
  item_category?: string;
};

// ─── Adapters ──────────────────────────────────────────────────────────
export function productToItem(p: Product, qty = 1): AnalyticsItem {
  return {
    item_id: p.id,
    item_name: `${p.brand} ${p.model} — ${p.colorway}`,
    price: p.priceCents / 100,
    quantity: qty,
    item_brand: p.brand,
    item_category: p.brand,
  };
}

export function lineToItem(line: CartLine): AnalyticsItem {
  return {
    item_id: line.productId,
    item_name: line.name,
    price: line.priceCents / 100,
    quantity: line.qty,
    item_brand: line.name.split(" ")[0],
  };
}

type GtagFn = (...args: unknown[]) => void;
type FbqFn = ((...args: unknown[]) => void) & { callMethod?: GtagFn };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

// ─── Init (idempotent) ─────────────────────────────────────────────────
const initialized = { ga: false, pixel: false };

export function initGA(id: string | undefined): void {
  if (!id || typeof window === "undefined") return;
  if (initialized.ga || typeof window.gtag === "function") {
    initialized.ga = true;
    return;
  }
  window.dataLayer = window.dataLayer || [];
  const gtag: GtagFn = function (...args) {
    (window.dataLayer as unknown[]).push(args);
  };
  window.gtag = gtag;
  // Consent Mode v2 default — deny everything until the user opts in via the
  // consent banner. CCPA-compliant + future-proof for any EU traffic that
  // wanders in. ConsentBanner.tsx reads localStorage and emits a consent
  // "update" event silently for return visitors who previously accepted.
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
  gtag("js", new Date());
  gtag("config", id, { send_page_view: true });
  initialized.ga = true;
}

export function initPixel(id: string | undefined): void {
  if (!id || typeof window === "undefined") return;
  if (initialized.pixel || typeof window.fbq === "function") {
    initialized.pixel = true;
    return;
  }
  // Standard Meta Pixel base — adapted to a no-loader form because the
  // <Script> tag in AnalyticsBootstrap handles fbevents.js loading.
  const fbq: FbqFn = function (...args) {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
    else (fbq as unknown as { queue: unknown[][] }).queue.push(args);
  } as FbqFn;
  (fbq as unknown as { queue: unknown[][] }).queue = [];
  (fbq as unknown as { loaded: boolean }).loaded = true;
  (fbq as unknown as { version: string }).version = "2.0";
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  fbq("init", id);
  // Consent Mode default for Meta — revoke until the consent banner grants.
  // ConsentBanner.tsx emits fbq("consent", "grant") for return visitors who
  // previously accepted.
  fbq("consent", "revoke");
  fbq("track", "PageView");
  initialized.pixel = true;
}

// ─── Price parsing ─────────────────────────────────────────────────────
/**
 * Normalize price strings ("$129.99", "129.99", "1,299.99") to a number.
 * Returns 0 for missing/unparseable input — analytics shouldn't throw on
 * a missing price.
 */
export function parsePrice(input: string | number | undefined): number {
  if (input == null) return 0;
  if (typeof input === "number") return Number.isFinite(input) ? input : 0;
  const cleaned = input.replace(/[^0-9.\-]/g, "");
  if (!cleaned) return 0;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

// ─── Helpers ───────────────────────────────────────────────────────────
function ga(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag(...args);
}

function fb(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  window.fbq(...args);
}

function sumValue(items: AnalyticsItem[]): number {
  return items.reduce((s, i) => s + i.price * i.quantity, 0);
}

function contentIds(items: AnalyticsItem[]): string[] {
  return items.map((i) => i.item_id);
}

function pixelContents(items: AnalyticsItem[]): Array<{ id: string; quantity: number }> {
  return items.map((i) => ({ id: i.item_id, quantity: i.quantity }));
}

// ─── Event trackers ────────────────────────────────────────────────────
export function trackViewItem(item: AnalyticsItem): void {
  const value = item.price * item.quantity;
  ga("event", "view_item", {
    currency: "USD",
    value,
    items: [item],
  });
  fb("track", "ViewContent", {
    content_ids: [item.item_id],
    content_name: item.item_name,
    content_type: "product",
    contents: pixelContents([item]),
    currency: "USD",
    value,
  });
}

export function trackAddToCart(item: AnalyticsItem): void {
  const value = item.price * item.quantity;
  ga("event", "add_to_cart", {
    currency: "USD",
    value,
    items: [item],
  });
  fb("track", "AddToCart", {
    content_ids: [item.item_id],
    content_name: item.item_name,
    content_type: "product",
    contents: pixelContents([item]),
    currency: "USD",
    value,
  });
}

export function trackRemoveFromCart(item: AnalyticsItem): void {
  const value = item.price * item.quantity;
  ga("event", "remove_from_cart", {
    currency: "USD",
    value,
    items: [item],
  });
  // Meta has no standard "remove from cart" — emit as a custom event so it
  // shows up in the Pixel diagnostics tab without polluting standard reports.
  fb("trackCustom", "RemoveFromCart", {
    content_ids: [item.item_id],
    content_type: "product",
    contents: pixelContents([item]),
    currency: "USD",
    value,
  });
}

export function trackViewCart(items: AnalyticsItem[]): void {
  if (items.length === 0) return;
  const value = sumValue(items);
  ga("event", "view_cart", {
    currency: "USD",
    value,
    items,
  });
  // Meta has no standard "view cart" — custom event.
  fb("trackCustom", "ViewCart", {
    content_ids: contentIds(items),
    content_type: "product",
    contents: pixelContents(items),
    currency: "USD",
    value,
  });
}

export function trackBeginCheckout(items: AnalyticsItem[]): void {
  if (items.length === 0) return;
  const value = sumValue(items);
  ga("event", "begin_checkout", {
    currency: "USD",
    value,
    items,
  });
  fb("track", "InitiateCheckout", {
    content_ids: contentIds(items),
    content_type: "product",
    contents: pixelContents(items),
    currency: "USD",
    value,
    num_items: items.reduce((s, i) => s + i.quantity, 0),
  });
}

export function trackPurchase(
  orderId: string,
  items: AnalyticsItem[],
  totalCents: number
): void {
  const value = totalCents / 100;
  ga("event", "purchase", {
    transaction_id: orderId,
    currency: "USD",
    value,
    items,
  });
  fb("track", "Purchase", {
    content_ids: contentIds(items),
    content_type: "product",
    contents: pixelContents(items),
    currency: "USD",
    value,
    order_id: orderId,
  });
}
